const WAITLIST_SELECTORS = {
  waitlistDialog: 'dialog-component[data-dialog]',
  eventDetailsSection: '[data-event-details]',
  formEmbed: 'shopify-forms-embed',
  appEmbed: '#app-embed',
  selectedEventInput: 'input[name="event-product"]:checked',
  nameFieldWrapper: 'div',
};

const WAITLIST_IDS = {
  nameField: 'custom#event_name',
};

const WAITLIST_ATTRIBUTES = {
  currentStep: 'data-current-step',
};

const WAITLIST_VALUES = {
  successStep: 'success',
};

const WAITLIST_DATA_ATTRIBUTES = {
  timeSlot: 'data-time-slot',
};

/**
 * @typedef {Object} WaitlistEventData
 * @property {string} [title]
 * @property {boolean} [express_interest]
 * @property {{ value?: string }[]} [time_slots]
 */

class EventWaitlistFormController extends HTMLElement {
  /** @type {(HTMLElement & { closeDialog?: () => void }) | null} */
  _waitlistDialog = null;

  /** @type {MutationObserver | null} */
  _observer = null;

  connectedCallback() {
    const waitlistDialog = this.querySelector(WAITLIST_SELECTORS.waitlistDialog);
    if (!(waitlistDialog instanceof HTMLElement)) return;
    this._waitlistDialog = waitlistDialog;

    this._waitlistDialog.addEventListener('dialog:open', this._handleDialogOpen);
  }

  disconnectedCallback() {
    if (this._observer) {
      this._observer.disconnect();
      this._observer = null;
    }

    if (this._waitlistDialog && this._handleDialogOpen) {
      this._waitlistDialog.removeEventListener('dialog:open', this._handleDialogOpen);
    }
  }

  _handleDialogOpen = () => {
    if (this._observer) {
      this._observer.disconnect();
    }

    const section = this.closest(WAITLIST_SELECTORS.eventDetailsSection);
    if (!(section instanceof HTMLElement)) return;

    const formEmbed = this._waitlistDialog?.querySelector(WAITLIST_SELECTORS.formEmbed);
    if (!(formEmbed instanceof HTMLElement) || !formEmbed.shadowRoot) return;
    
    const appEmbed = formEmbed.shadowRoot.querySelector(WAITLIST_SELECTORS.appEmbed);
    if (!(appEmbed instanceof HTMLElement)) return;

    this._setDefaultNameValue(formEmbed, section);
    this._hideNameField(formEmbed);
    this._setFormEmbedStyles(formEmbed);

    this._observer = new MutationObserver((mutationList) => {
      for (const mutation of mutationList) {
        const target = mutation.target;
        if (
          mutation.type === 'attributes' &&
          mutation.attributeName === WAITLIST_ATTRIBUTES.currentStep &&
          target instanceof Element &&
          target.getAttribute(WAITLIST_ATTRIBUTES.currentStep) === WAITLIST_VALUES.successStep
        ) {
          this._waitlistDialog?.closeDialog?.();
          this._observer?.disconnect();
          return;
        }
      }

      this._setDefaultNameValue(formEmbed, section);
    });

    this._observer.observe(appEmbed, {
      attributes: true,
      childList: true,
      subtree: true,
    });
  };

  /**
   * @param {HTMLElement} section
   * @returns {{ timeSlot: string }}
   */
  _getSelectedDataAttributes(section) {
    const checkedInput = section.querySelector(WAITLIST_SELECTORS.selectedEventInput);
    if (!(checkedInput instanceof HTMLInputElement)) {
      const windowWithEventData = /** @type {Window & { eventData?: WaitlistEventData }} */ (window);
      const timeSlot = windowWithEventData.eventData?.time_slots?.[0]?.value || '';
      if (!timeSlot) return { timeSlot: '' };
      return { timeSlot };
    }

    return {
      timeSlot: checkedInput.getAttribute(WAITLIST_DATA_ATTRIBUTES.timeSlot) || '',
    };
  }

  /**
   * @param {HTMLElement} section
   * @returns {string}
   */
  _buildDefaultName(section) {
    const windowWithEventData = /** @type {Window & { eventData?: WaitlistEventData }} */ (window);
    const eventTitle = windowWithEventData.eventData?.title || '';
    if (!eventTitle) return '';

    if (windowWithEventData.eventData?.express_interest === true) {
      return eventTitle;
    }

    const { timeSlot } = this._getSelectedDataAttributes(section);
    if (!timeSlot) return '';
    return `${eventTitle} — ${timeSlot}`;
  }

  /**
   * @param {HTMLElement} formEmbed
   * @param {HTMLElement} section
   * @returns {void}
   */
  _setDefaultNameValue(formEmbed, section) {
    const shadowRoot = formEmbed.shadowRoot;
    if (!shadowRoot) return;

    const nameField = shadowRoot.getElementById(WAITLIST_IDS.nameField);
    if (!(nameField instanceof HTMLInputElement)) return;

    const defaultName = this._buildDefaultName(section);
    if (!defaultName || nameField.value === defaultName) return;

    nameField.value = defaultName;
    nameField.defaultValue = defaultName;
    nameField.dispatchEvent(new Event('input', { bubbles: true }));
  }

  /**
   * @param {HTMLElement} formEmbed
   * @returns {void}
   */
  _hideNameField(formEmbed) {
    const shadowRoot = formEmbed.shadowRoot;
    if (!shadowRoot) return;

    const nameField = shadowRoot.getElementById(WAITLIST_IDS.nameField);
    if (!(nameField instanceof HTMLInputElement)) return;

    const fieldWrapper = nameField.closest(WAITLIST_SELECTORS.nameFieldWrapper);
    if (fieldWrapper instanceof HTMLElement) {
      fieldWrapper.style.setProperty('display', 'none');
    }
  }

  /**
   * @param {HTMLElement} formEmbed
   * @returns {void}
   */
  _setFormEmbedStyles(formEmbed) {
    formEmbed.style.setProperty('--input-label-top', '2em');
  }
}

if (!customElements.get('event-waitlist-form-controller')) {
  customElements.define('event-waitlist-form-controller', EventWaitlistFormController);
}
